import { projectId } from './supabase/info';
import { cachedFetch } from './fetchWithCache';

import { bearerAuthHeader } from './apiHeaders';
/**
 * Extract tenant subdomain or custom domain from current hostname
 * Examples:
 * - acme.pathkit.pro -> "acme" (subdomain)
 * - pathkit.acmeconsulting.com -> "pathkit.acmeconsulting.com" (custom domain)
 * - localhost:5173 -> null (development)
 * - app.pathkit.pro -> null (default app access)
 * - admin.pathkit.pro -> null (system admin access)
 */
export function getTenantFromHostname(): string | null {
  if (typeof window === 'undefined') return null;
  
  const hostname = window.location.hostname;
  
  // Development with subdomains (e.g., acme.localhost)
  if (hostname.endsWith('.localhost') || hostname.includes('.localhost:')) {
    const parts = hostname.split('.');
    if (parts.length >= 2 && parts[0] !== 'www') {
      return parts[0]; // Return subdomain part
    }
  }
  
  // Development without subdomain (plain localhost)
  if (hostname === 'localhost' || hostname.startsWith('192.168') || hostname.startsWith('127.0.0.1')) {
    // Check for ?tenant= query parameter in development
    const params = new URLSearchParams(window.location.search);
    return params.get('tenant');
  }
  
  // Default app access (no tenant) - including admin subdomain for system admin access
  if (hostname === 'app.pathkit.pro' || hostname === 'pathkit.pro' || hostname === 'www.pathkit.pro' ||
      hostname === 'staging.pathkit.pro' || hostname === 'admin.pathkit.pro') {
    return null;
  }
  
  // Vercel preview deployments and Figma preview
  if (hostname.includes('.vercel.app') || hostname.includes('.figma.site')) {
    // Check for ?tenant= query parameter
    const params = new URLSearchParams(window.location.search);
    return params.get('tenant');
  }
  
  // Check if it's a pathkit.pro subdomain
  if (hostname.endsWith('.pathkit.pro')) {
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      const subdomain = parts[0];
      
      // Ignore common subdomains (including 'staging' which is the staging environment)
      if (['www', 'app', 'api', 'admin', 'staging', 'dev', 'test'].includes(subdomain)) {
        return null;
      }
      
      return subdomain;
    }
  }
  
  // If it's not a pathkit.pro domain, it might be a custom domain
  // Return the full hostname to lookup
  if (hostname !== 'pathkit.pro') {
    return hostname;
  }
  
  return null;
}

/**
 * Check if current hostname is the system admin domain
 */
export function isAdminDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'admin.pathkit.pro';
}

/**
 * Check if current hostname is a root/admin domain (where system admins can login)
 */
export function isSystemAdminDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname === 'pathkit.pro' || 
         hostname === 'www.pathkit.pro' || 
         hostname === 'admin.pathkit.pro' ||
         hostname === 'staging.pathkit.pro' ||
         hostname === 'localhost' ||
         hostname.includes('figmaiframepreview');
}

/**
 * Check if tenant identifier is a custom domain
 */
export function isCustomDomain(tenant: string): boolean {
  // If it contains dots and doesn't end with pathkit.pro, it's a custom domain
  return tenant.includes('.') && !tenant.endsWith('.pathkit.pro');
}

/**
 * Get the consulting firm ID and details from subdomain or custom domain
 * All tenant subdomains represent consulting firms (not individual client companies)
 */
export async function getTenantCompanyId(tenant: string): Promise<{ firmId: string; firmName: string } | null> {
  try {
    console.log('[FRONTEND_TENANT_LOOKUP] ========================================');
    console.log('[FRONTEND_TENANT_LOOKUP] Starting lookup for tenant:', tenant);
    console.log('[FRONTEND_TENANT_LOOKUP] Current hostname:', window.location.hostname);
    console.log('[FRONTEND_TENANT_LOOKUP] Current full URL:', window.location.href);
    console.log('[FRONTEND_TENANT_LOOKUP] Supabase project:', projectId.substring(0, 10) + '...');
    
    let endpoint: string;
    
    // Check if it's a custom domain or subdomain
    if (isCustomDomain(tenant)) {
      // Lookup by custom domain
      endpoint = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/public/lookup-domain/${tenant}`;
      console.log('[FRONTEND_TENANT_LOOKUP] Tenant type: CUSTOM DOMAIN');
      console.log('[FRONTEND_TENANT_LOOKUP] Endpoint: /public/lookup-domain/:domain');
    } else {
      // Lookup by subdomain - FIXED: was /public/tenant, should be /tenant
      endpoint = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/tenant/${tenant}`;
      console.log('[FRONTEND_TENANT_LOOKUP] Tenant type: SUBDOMAIN');
      console.log('[FRONTEND_TENANT_LOOKUP] Endpoint: /tenant/:subdomain');
    }
    
    console.log('[FRONTEND_TENANT_LOOKUP] Full endpoint URL:', endpoint.replace(projectId, projectId.substring(0, 10) + '...'));
    console.log('[FRONTEND_TENANT_LOOKUP] Calling backend...');
    
    const response = await cachedFetch(endpoint, {
      headers: {
        ...bearerAuthHeader()
      }
    });
    
    console.log('[FRONTEND_TENANT_LOOKUP] Response status:', response.status);
    console.log('[FRONTEND_TENANT_LOOKUP] Response ok:', response.ok);
    
    if (!response.ok) {
      console.log('[FRONTEND_TENANT_LOOKUP] FAILED - Lookup failed with status', response.status);
      const errorText = await response.text();
      console.log('[FRONTEND_TENANT_LOOKUP] Error response body:', errorText);
      console.log('[FRONTEND_TENANT_LOOKUP] ========================================');
      return null;
    }
    
    const data = await response.json();
    console.log('[FRONTEND_TENANT_LOOKUP] Response data received:', JSON.stringify(data, null, 2));
    console.log('[FRONTEND_TENANT_LOOKUP] Extracted firmId:', data.firmId || data.companyId);
    console.log('[FRONTEND_TENANT_LOOKUP] Extracted firmName:', data.firmName || data.companyName);
    
    const result = {
      firmId: data.firmId || data.companyId,
      firmName: data.firmName || data.companyName
    };
    
    console.log('[FRONTEND_TENANT_LOOKUP] SUCCESS - Returning to app:', JSON.stringify(result, null, 2));
    console.log('[FRONTEND_TENANT_LOOKUP] ========================================');
    
    return result;
  } catch (error) {
    console.error('[FRONTEND_TENANT_LOOKUP] Exception caught:', error);
    console.error('[FRONTEND_TENANT_LOOKUP] Exception type:', error.constructor.name);
    console.error('[FRONTEND_TENANT_LOOKUP] Exception message:', error.message);
    if (error.stack) {
      console.error('[FRONTEND_TENANT_LOOKUP] Exception stack:', error.stack);
    }
    console.log('[FRONTEND_TENANT_LOOKUP] ========================================');
    return null;
  }
}

/**
 * Check if current hostname is a tenant subdomain
 */
export function isTenantSubdomain(): boolean {
  return getTenantFromHostname() !== null;
}

/**
 * Get the full URL for a tenant subdomain
 */
export function getTenantUrl(subdomain: string): string {
  if (window.location.hostname === 'localhost' || 
      window.location.hostname.includes('.vercel.app') || 
      window.location.hostname.includes('.figma.site')) {
    // In development or preview, use query parameter
    return `${window.location.origin}?tenant=${subdomain}`;
  }
  
  // In production, use actual subdomain
  return `https://${subdomain}.pathkit.pro`;
}

/**
 * Get the display URL for a company (custom domain or subdomain)
 */
export function getCompanyURL(company: any): string {
  if (company.customDomainEnabled && company.customDomain) {
    return `https://${company.customDomain}`;
  }
  if (company.subdomainEnabled && company.subdomain) {
    return `https://${company.subdomain}.pathkit.pro`;
  }
  return 'https://app.pathkit.pro';
}